import React, { useState } from 'react';

// Preserver holding gauge — MOBILE-FIRST. Visualizes the price journey on a
// stop→high-water-mark band: the green fill is your CUSHION above the trailing
// stop; the empty gap on the right is how far price has pulled back from its
// high. Entry (thin tick) and today (solid marker) are placed within the band.
// Full-width, no hover dependency (labels always visible), legible at 360px.
function HoldingGauge({ entry, now, hwm, stop }) {
  const lo = Number(stop) || 0;
  const hi = Math.max(Number(hwm) || 0, lo + 0.01);
  const range = (hi - lo) || 1;
  const clamp = (x) => Math.max(0, Math.min(100, ((Number(x) || lo) - lo) / range * 100));
  const nowPct = clamp(now);
  const entryPct = clamp(entry);
  const cushion = now > 0 ? Math.max(0, (now - lo) / now * 100) : 0;
  return (
    <div>
      <div className="relative h-2.5 rounded-full bg-paper-deep">
        {/* cushion fill: stop → today */}
        <div className="absolute inset-y-0 left-0 rounded-full bg-positive/35" style={{ width: `${nowPct}%` }} />
        {/* entry tick */}
        <div className="absolute top-1/2 -translate-y-1/2 h-3.5 w-px bg-ink-mute/70"
             style={{ left: `${entryPct}%` }} title={`Entry $${Number(entry).toFixed(2)}`} />
        {/* today marker */}
        <div className="absolute top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-full bg-ink"
             style={{ left: `calc(${nowPct}% - 1.5px)` }} title={`Today $${Number(now).toFixed(2)}`} />
      </div>
      <div className="flex items-center justify-between mt-1.5 font-mono text-[0.58rem] leading-none text-ink-mute">
        <span title="Trailing stop">stop ${Math.round(lo)}</span>
        <span className="text-claret font-medium">{cushion.toFixed(0)}% cushion to stop</span>
        <span title="High-water mark">high ${Math.round(hi)}</span>
      </div>
    </div>
  );
}

// Capital-scaled MIRROR of a tier's model book. The user sets their capital once; we scale
// the book's positions to it (implied_shares = book_shares x capital/book_value) so their
// portfolio auto-mirrors the book with zero per-trade entry. Maximizer = breakout book
// (day-X/29 exits); Preserver = t30v book (30% trailing). (Jul 24 2026)
// Why a book position left today (Preserver sells carry exit_reason; Maximizer's are day-29 exits).
// Corporate events read as "acquired"/etc. — the broker already converted those, nothing to sell.
const SELL_REASON = {
  trailing_stop: '30% trailing stop',
  market_regime: 'S&P below 200-day',
  cash_merger: 'acquired — cash, nothing to sell',
  stock_merger: 'acquired — stock, nothing to sell',
  stock_and_cash_merger: 'acquired — nothing to sell',
  spin_off: 'spin-off',
  renamed: 'ticker change',
  redemption: 'redeemed',
  worthless_removal: 'removed',
};

// Book value now (Oct 29 2026): holdings at live prices + cash, compounded by the book's return
// since the user joined (the mirror is re-sized to book size nightly). Shared by the book header
// and the phone two-book summary so both always show the same number.
export function bookNow(book) {
  if (!book) return { value: 0, today: null };
  const markToday = (Number(book.invested_value) || 0) + (Number(book.cash_value) || 0);
  const growth = book.since_joined?.pct != null ? 1 + book.since_joined.pct / 100 : 1;
  return { value: markToday * growth, today: book._today_change != null ? book._today_change * growth : null };
}

export default function TierBookView({ book, onSetCapital, onRowClick, radar, actions, hideCapitalEditor = false, compact = false, marketNote = null, isHeld = () => false }) {
  // Green "you hold this" dot — the same held-language as the Mirror, on the mirror book.
  const HeldDot = ({ sym }) => (isHeld(sym)
    ? <span title="In your portfolio" aria-label="You hold this" className="text-positive text-[0.6rem] mr-1.5 align-middle">●</span>
    : null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(book?.capital ?? 100000));
  const [saving, setSaving] = useState(false);
  const [capError, setCapError] = useState('');
  // One-time note when the book sits on the $100,000 default (Oct 30 2026: default moved from
  // $10,000 → explains the jump to existing users, and invites new users to set their own amount).
  const SIZE_NOTE_KEY = 'rigacap_size_note_seen';
  const [sizeNoteSeen, setSizeNoteSeen] = useState(() => {
    try { return localStorage.getItem(SIZE_NOTE_KEY) === '1'; } catch { return false; }
  });
  const dismissSizeNote = () => {
    setSizeNoteSeen(true);
    try { localStorage.setItem(SIZE_NOTE_KEY, '1'); } catch { /* ignore */ }
  };

  if (!book) return null;
  const isMax = book.tier === 'maximizer';
  // Two-book view (Maximizer subscriber): one "sized to" amount drives BOTH books — say so, so
  // nobody reads two $10,000 books as $20,000 we're tracking. We mirror; we don't see accounts.
  const twoBook = compact || hideCapitalEditor;
  const eachBook = twoBook ? ' each book' : '';
  const usd0 = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  // Shares are the convenience conversion of the (exact) dollar target — most people place
  // whole-share orders and fractional 2-decimals reads as false precision. Round to whole;
  // the "<1" guard flags names where the account is too small to hold a full share cleanly.
  const sh = (v) => {
    if (v == null) return '—';
    const n = Number(v);
    if (n > 0 && n < 1) return '<1';
    return Math.round(n).toLocaleString();
  };

  // Never fail silently (Oct 2 2026: an edit "flipped back" with no message). Invalid input or a
  // failed save keeps the editor open with an error; only a confirmed save closes it.
  const save = async () => {
    const val = Number(String(draft).replace(/[^0-9.]/g, ''));
    if (!(val >= 100 && val <= 10000000)) { setCapError('Enter an amount between $100 and $10,000,000.'); return; }
    setSaving(true);
    setCapError('');
    try {
      await onSetCapital(val);
      setEditing(false);
    } catch (e) {
      setCapError("Couldn't save — please try again.");
    } finally {
      setSaving(false);
    }
  };
  const cancelEdit = () => { setEditing(false); setCapError(''); };

  return (
    <div className="border border-rule rounded bg-paper-card mb-6">
      {/* Header */}
      <div className="px-4 sm:px-5 py-4 border-b border-rule flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-[1.2rem] font-medium text-ink tracking-tight flex items-center gap-2" style={{ fontVariationSettings: '"opsz" 48' }}>
            {isMax && <span className="text-claret text-[0.7rem]">◆</span>}
            Your {isMax ? 'Maximizer' : 'Preserver'} Book
          </h2>
          <p className="hidden sm:block font-display italic text-[0.82rem] text-ink-mute mt-0.5" style={{ fontVariationSettings: '"opsz" 24' }}>
            Auto-mirrored to the model book — no manual entry. {isMax ? 'Breakouts, held 29 trading days.' : '30% trailing stop, let winners run.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {book.new_today > 0 && (
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.14em] bg-claret text-paper px-2 py-0.5 rounded">
              {book.new_today} new today
            </span>
          )}
          {/* Regime chip removed from the book header — it lives in the top regime bar, and the
              extra line made the two book headers different heights (market-read / capital rows
              no longer aligned in the side-by-side). */}
        </div>
      </div>

      {/* Market read — this book's own view (Preserver = measured regime read; Maximizer =
          breakout-posture briefing). Shown under the header in the two-book side-by-side. */}
      {marketNote && (
        /* data-market-read: the parent (Dashboard) MEASURES both books' reads and pins them to
           the taller one so the Your Capital ribbons below line up pixel-perfect — min-height
           can't guarantee it when the two reads differ in length. md+ only; mobile stacks. */
        <div data-market-read className="hidden sm:block px-4 sm:px-5 py-2.5 border-b border-rule bg-paper-deep/40">
          <span className="font-body text-[0.56rem] font-medium tracking-[0.18em] uppercase text-ink-mute">Market read</span>
          <p className="font-display italic text-[0.82rem] text-ink-mute mt-0.5 leading-snug" style={{ fontVariationSettings: '"opsz" 24' }}>{marketNote}</p>
        </div>
      )}

      {/* Book value now — holdings at live prices + cash, today's move, and the book's return
          since the user joined (Oct 29 2026: we showed what was invested, never what it's worth). */}
      {(() => {
        // COMPOUNDING value (Oct 29 2026): the mirror is re-sized to the book size every night, so
        // holdings + cash alone always reset to that number. What it's worth if you'd mirrored since
        // joining = book size grown by the book's return since then, times today's live move.
        const { value, today } = bookNow(book);
        const todayPct = today != null && value - today > 0 ? (today / (value - today)) * 100 : null;
        const sj = book.since_joined;
        const sjFrom = sj?.from ? new Date(sj.from + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
        const sign = (v) => (v >= 0 ? '+' : '−');
        const tone = (v) => (v >= 0 ? 'text-positive' : 'text-negative');
        return (
          <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-rule">
            <div className="flex items-baseline gap-2">
              <span className="text-[0.6rem] uppercase tracking-[0.16em] text-ink-mute">Value now</span>
              {book._intraday && <span className="font-mono text-[0.55rem] tracking-[0.14em] uppercase text-positive border border-positive/40 px-1.5 py-px">Live</span>}
            </div>
            <div className="font-display text-[2rem] sm:text-[2.2rem] leading-tight text-ink" style={{ fontVariationSettings: '"opsz" 72' }}>{usd0(value)}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-0.5 font-mono text-[0.78rem]">
              {today != null && (
                <span><span className="text-ink-mute">Today</span> <span className={tone(today)}>{sign(today)}{usd0(Math.abs(today))}{todayPct != null ? ` (${sign(todayPct)}${Math.abs(todayPct).toFixed(1)}%)` : ''}</span></span>
              )}
              {sj && sj.pct != null && (
                <span><span className="text-ink-mute">Since {sjFrom || 'you joined'}</span> <span className={tone(sj.pct)}>{sign(sj.pct)}{usd0(Math.abs((Number(book.capital) || 0) * sj.pct / 100))} ({sign(sj.pct)}{Math.abs(sj.pct).toFixed(1)}%)</span></span>
              )}
            </div>
            {sj && sj.pct != null && (
              <div className="mt-1 font-body text-[0.68rem] text-ink-mute">Sized to {usd0(book.capital)}, mirrored since {sjFrom}.</div>
            )}
          </div>
        );
      })()}

      {/* Phones, quiet day: say so plainly (mock A "Today's moves"). */}
      {actions && !((actions.buys || []).length > 0 || (actions.sells || []).length > 0 || actions.rebalance) && (
        <div className="sm:hidden px-4 py-2.5 border-b border-rule bg-paper-deep">
          <span className="font-body text-[0.6rem] font-medium tracking-[0.18em] uppercase text-claret">Today's moves</span>
          <div className="font-display text-[1rem] text-ink mt-0.5" style={{ fontVariationSettings: '"opsz" 32' }}>Nothing to do. Hold all {book.holdings?.length || 0}.</div>
        </div>
      )}

      {/* Today's moves — one plain line per kind of move (Oct 30 2026: the old ribbon ran buys,
          sells and every per-name trim together in monospace and read like code). The per-name
          trim amounts fold behind a tap. */}
      {actions && ((actions.buys || []).length > 0 || (actions.sells || []).length > 0 || actions.rebalance) && (() => {
        const rb = actions.rebalance;
        const trim = rb?.side === 'trim';
        const Tag = ({ children, tone }) => (
          <span className={`inline-block w-12 shrink-0 text-center font-body text-[0.62rem] font-semibold tracking-[0.12em] uppercase py-0.5 ${tone}`}>{children}</span>
        );
        const sellWhy = (actions.sells || []).every(x => !x.exit_reason || x.exit_reason === 'hold_exit')
          ? '29-day hold is up' : (actions.sells || []).map(x => SELL_REASON[x.exit_reason] || 'exit').join(', ');
        return (
          <div className="px-4 sm:px-5 py-3 border-b border-rule bg-paper-deep">
            <div className="font-body text-[0.6rem] font-medium tracking-[0.18em] uppercase text-claret mb-2">Today's moves</div>
            <div className="flex flex-col gap-2 font-body text-[0.88rem] text-ink">
              {(actions.sells || []).length > 0 && (
                <div className="flex items-baseline gap-2.5">
                  <Tag tone="bg-claret text-paper">Sell</Tag>
                  <span><strong className="font-semibold">{actions.sells.map(x => x.symbol).join(', ')}</strong> <span className="text-ink-mute">· {sellWhy}</span></span>
                </div>
              )}
              {(actions.buys || []).length > 0 && (
                <div className="flex items-baseline gap-2.5">
                  <Tag tone="bg-positive text-paper">Buy</Tag>
                  <span><strong className="font-semibold">{actions.buys.map(x => x.symbol).join(', ')}</strong> <span className="text-ink-mute">· new breakout{actions.buys.length > 1 ? 's' : ''}</span></span>
                </div>
              )}
              {rb && (
                <div className="flex items-baseline gap-2.5">
                  <Tag tone="border border-claret text-claret">{trim ? 'Trim' : 'Add'}</Tag>
                  <span>
                    <strong className="font-semibold">{trim ? 'Every other holding' : 'Every holding'} by about {Math.abs(rb.pct).toFixed(0)}%</strong>
                    <span className="text-ink-mute"> · {trim
                      ? `the market got choppier, so the book holds less (${Math.round(rb.from * 100)}% → ${Math.round(rb.to * 100)}% invested). Keep the proceeds in cash.`
                      : `the market calmed down, so the book holds more (${Math.round(rb.from * 100)}% → ${Math.round(rb.to * 100)}% invested), from cash.`}</span>
                  </span>
                </div>
              )}
              {/* No per-holding share list (Erik, Oct 30 2026): we don't see their real positions, so
                  the % is the honest instruction — it works whatever they actually hold. */}
            </div>
          </div>
        );
      })()}

      {!sizeNoteSeen && !hideCapitalEditor && Number(book.capital) === 100000 && !editing && (
        <div className="px-4 sm:px-5 py-2.5 border-b border-rule bg-paper-card flex items-start gap-3 font-body text-[0.82rem] text-ink">
          <span className="flex-1 leading-relaxed">
            Your book is now sized to <strong>$100,000</strong>, our new default. Mirroring with a different amount?{' '}
            <button type="button" onClick={() => { dismissSizeNote(); setDraft(String(book.capital)); setCapError(''); setEditing(true); }}
                    className="text-claret underline underline-offset-2">Set yours</button>
          </span>
          <button type="button" onClick={dismissSizeNote} aria-label="Dismiss" className="text-ink-mute hover:text-ink px-1">✕</button>
        </div>
      )}

      {/* Phones: one summary line instead of the 2x2 grid (mock A, Oct 29). Tapping "edit" opens
          the grid's editor. */}
      {!editing && (
        <div className="sm:hidden px-4 py-2.5 border-b border-rule font-body text-[0.8rem] text-ink-mute leading-relaxed">
          {book.holdings?.length || 0} holdings · {usd0(book.invested_value)} in stocks · {usd0(book.cash_value)} cash · sized to {usd0(book.capital)}{twoBook ? ' each book' : ''}
          {!hideCapitalEditor && (
            <button onClick={() => { setDraft(String(book.capital)); setCapError(''); setEditing(true); }} className="ml-1.5 text-claret underline underline-offset-2">edit</button>
          )}
        </div>
      )}

      {/* Capital + summary */}
      <div className={`${editing ? 'grid' : 'hidden sm:grid'} px-4 sm:px-5 py-4 grid-cols-2 sm:grid-cols-4 gap-4 border-b border-rule`}>
        {/* While editing, the editor takes the whole row (Nov 2 2026: it overflowed into "In stocks"). */}
        <div className={editing && !hideCapitalEditor ? 'col-span-2 sm:col-span-4' : ''}>
          <div className="text-[0.6rem] uppercase tracking-[0.16em] text-ink-mute mb-1">Sized to{eachBook && <span className="normal-case tracking-normal"> · each book</span>}</div>
          {hideCapitalEditor ? (
            /* Shared-capital mode (two books side-by-side): show the value, edit lives on the
               other book so one control drives both. */
            <div className="font-mono text-[1.15rem] text-ink">{usd0(book.capital)}</div>
          ) : editing ? (
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center border border-ink/40 focus-within:border-claret rounded bg-paper px-2.5">
                  <span className="font-mono text-[1rem] text-ink-mute">$</span>
                  <input
                    autoFocus inputMode="numeric"
                    value={(() => { const d = String(draft).replace(/[^0-9]/g, ''); return d ? Number(d).toLocaleString() : ''; })()}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => { setDraft(e.target.value.replace(/[^0-9]/g, '')); setCapError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') save(); else if (e.key === 'Escape') cancelEdit(); }}
                    aria-label="Amount you mirror this book with, in dollars"
                    className="w-36 bg-transparent px-1.5 py-1.5 font-mono text-[1.05rem] text-ink outline-none"
                  />
                </div>
                <button onClick={save} disabled={saving}
                  className="font-body text-[0.85rem] font-medium bg-claret text-paper px-4 py-1.5 rounded hover:bg-claret-light transition-colors disabled:opacity-60">
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={cancelEdit} className="font-body text-[0.85rem] text-ink-mute hover:text-ink underline underline-offset-2">Cancel</button>
              </div>
              {capError
                ? <div role="alert" className="font-body text-[0.68rem] text-claret mt-1">{capError}</div>
                : <div className="font-body text-[0.68rem] text-ink-mute mt-1 leading-snug max-w-[16rem]">The amount you mirror {twoBook ? 'each book' : 'this book'} with. We don't see your account, so this is your number to set. Change it anytime &mdash; new entries are sized to the new amount.</div>}
            </div>
          ) : (
            <button onClick={() => { setDraft(String(book.capital)); setCapError(''); setEditing(true); }} className="font-mono text-[1.15rem] text-ink hover:text-claret transition-colors">
              {usd0(book.capital)} <span className="text-[0.6rem] text-ink-mute">edit</span>
            </button>
          )}
        </div>
        <div>
          <div className="text-[0.6rem] uppercase tracking-[0.16em] text-ink-mute mb-1">In stocks</div>
          <div className="font-mono text-[1.15rem] text-ink">{usd0(book.invested_value)}</div>
          <div className="font-mono text-[0.6rem] text-ink-mute">{book.invested_pct}% of book</div>
        </div>
        <div>
          <div className="text-[0.6rem] uppercase tracking-[0.16em] text-ink-mute mb-1">Cash</div>
          <div className="font-mono text-[1.15rem] text-ink">{usd0(book.cash_value)}</div>
          <div className="font-mono text-[0.6rem] text-ink-mute">{book.cash_pct}%</div>
        </div>
        <div>
          <div className="text-[0.6rem] uppercase tracking-[0.16em] text-ink-mute mb-1">Holdings</div>
          <div className="font-mono text-[1.15rem] text-ink">{book.holdings?.length || 0}</div>
          <div className="font-mono text-[0.6rem] text-ink-mute">names</div>
        </div>
      </div>

      {/* Phones: the market read sits AFTER the value + numbers, clamped (Oct 29 — it pushed the
          value a screen down). Desktop shows it under the header (above). */}
      {marketNote && (
        <details className="group sm:hidden px-4 py-2.5 border-b border-rule bg-paper-deep/40">
          <summary className="list-none cursor-pointer">
            <span className="font-body text-[0.56rem] font-medium tracking-[0.18em] uppercase text-ink-mute">Market read</span>
            <p className="font-display italic text-[0.82rem] text-ink-mute mt-0.5 leading-snug line-clamp-2 group-open:hidden" style={{ fontVariationSettings: '"opsz" 24' }}>{marketNote}</p>
          </summary>
          <p className="font-display italic text-[0.82rem] text-ink-mute mt-1 leading-snug" style={{ fontVariationSettings: '"opsz" 24' }}>{marketNote}</p>
        </details>
      )}

      {/* Vol-Target exposure gauge (Maximizer only) — the Barroso vol-brake. Sits AFTER the
          capital row so it lines up with the Preserver book's capital row in the side-by-side. */}
      {book.vol_scale != null && (
        <div className="px-4 sm:px-5 py-3 border-b border-rule">
          <div className="flex items-center justify-between mb-1">
            <span className="font-body text-[0.6rem] font-medium tracking-[0.18em] uppercase text-ink-mute">Vol-target exposure</span>
            <span className="font-mono text-[0.82rem] text-ink">{Math.round(book.vol_scale * 100)}%{book.vol_scale >= 0.999 ? ' · full' : ' · trimming risk'}</span>
          </div>
          <div className="h-2 bg-paper-deep rounded overflow-hidden">
            <div className="h-full bg-claret" style={{ width: `${Math.min(100, Math.round(book.vol_scale * 100))}%` }} />
          </div>
          <div className="mt-1.5 font-display italic text-[0.72rem] text-ink-light" style={{ fontVariationSettings: '"opsz" 24' }}>
            Auto-adjusts to keep risk steady — 100% is full exposure; it dials back (more cash, automatically) when breakouts turn volatile.
          </div>
        </div>
      )}

      {/* Holdings — Maximizer uses the compact table + 29-day hold-clock. Preserver uses the
          MOBILE-FIRST gauge cards standalone, but in `compact` mode (side-by-side two-book view)
          it renders the SAME table with a cushion-to-stop bar in the Exit column, so the two
          books line up row-for-row. No horizontal scroll on a phone — 76% of traffic is mobile. */}
      {(isMax || compact) ? (
        <>
        {/* Phones: compact holding cards (mock A); the table stays for sm+. */}
        <div className="sm:hidden">
          {(book.holdings || []).length === 0 ? (
            <div className="px-4 py-6 text-center font-display italic text-ink-mute text-[0.9rem]" style={{ fontVariationSettings: '"opsz" 24' }}>
              The book is in cash right now — no positions held.
            </div>
          ) : (book.holdings || []).map((h) => {
            const up = h.pnl_pct >= 0;
            return (
              <div key={h.symbol} onClick={() => onRowClick && onRowClick(h)}
                   className={`px-4 py-3.5 border-b border-rule/60 ${onRowClick ? 'cursor-pointer active:bg-paper-deep' : ''}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <HeldDot sym={h.symbol} />
                    <span className="font-display text-[1.1rem] font-medium text-ink" style={{ fontVariationSettings: '"opsz" 32' }}>{h.symbol}</span>
                    {h.is_new && <span className="font-mono text-[0.6rem] uppercase tracking-[0.14em] text-claret border border-claret/40 px-1 py-0.5">New</span>}
                    <span className="font-mono text-[0.72rem] text-ink-mute whitespace-nowrap">{h.weight_pct}% of book</span>
                  </div>
                  <span className={`font-mono text-[0.95rem] whitespace-nowrap ${up ? 'text-positive' : 'text-negative'}`}>{up ? '+' : ''}{h.pnl_pct}%</span>
                </div>
                {isMax ? (
                  <>
                    <div className="mt-2 h-1.5 bg-paper-deep rounded overflow-hidden">
                      <div className={`h-full ${h.days_left <= 5 ? 'bg-claret' : 'bg-claret/50'}`}
                           style={{ width: `${Math.min(100, Math.round((h.days_held / (h.hold_days || 29)) * 100))}%` }} />
                    </div>
                    <div className="flex justify-between gap-2 mt-1.5 font-mono text-[0.72rem] text-ink-mute">
                      <span>day {h.days_held} of {h.hold_days} · sells in ~{h.days_left}d</span>
                      <span className="text-ink whitespace-nowrap">{sh(h.implied_shares)} sh · {usd0(h.implied_value)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-2"><HoldingGauge entry={h.entry_price} now={h.price} hwm={h.high_water_mark} stop={h.trailing_stop_level} /></div>
                    <div className="flex justify-end mt-1.5 font-mono text-[0.72rem] text-ink whitespace-nowrap">{sh(h.implied_shares)} sh · {usd0(h.implied_value)}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full" style={{ fontFeatureSettings: '"tnum"' }}>
            <thead>
              <tr className="text-[0.58rem] uppercase tracking-[0.16em] text-ink-mute border-b border-rule">
                <th className="text-left py-2 pl-3 pr-2 sm:pl-4">Symbol</th>
                <th className="text-right py-2 px-2 2xl:px-3 hidden sm:table-cell">Weight</th>
                <th className="text-right py-2 px-2 2xl:px-3">Price</th>
                <th className="text-right py-2 px-2 2xl:px-3">Shares</th>
                <th className="text-right py-2 px-2 2xl:px-3">Value</th>
                <th className="text-left py-2 px-2 2xl:px-3">Exit</th>
                <th className="text-right py-2 px-2 2xl:px-3 hidden sm:table-cell" title={book?._intraday ? 'Live intraday prices' : 'As of the last close'}>P&L{book?._intraday ? <span className="ml-1 normal-case tracking-normal text-positive">· live</span> : null}</th>
              </tr>
            </thead>
            <tbody>
              {(book.holdings || []).map((h) => (
                <tr
                  key={h.symbol}
                  onClick={() => onRowClick && onRowClick(h)}
                  className={`border-b border-rule/50 ${onRowClick ? 'cursor-pointer hover:bg-paper-deep transition-colors' : ''}`}
                >
                  <td className="py-2.5 pl-3 pr-2 sm:pl-4">
                    <HeldDot sym={h.symbol} />
                    <span className="font-display text-[1rem] font-medium text-ink" style={{ fontVariationSettings: '"opsz" 32' }}>{h.symbol}</span>
                    {h.is_new && (
                      <span className="font-mono text-[0.55rem] uppercase tracking-[0.14em] text-claret border border-claret/40 px-1 py-0.5 ml-1.5 align-middle">New</span>
                    )}
                  </td>
                  <td className="py-2.5 px-2 2xl:px-3 text-right font-mono text-[0.82rem] text-ink-mute hidden sm:table-cell">{h.weight_pct}%</td>
                  <td className="py-2.5 px-2 2xl:px-3 text-right font-mono text-[0.82rem]">${h.price?.toFixed(2)}</td>
                  <td className="py-2.5 px-2 2xl:px-3 text-right font-mono text-[0.9rem] text-ink">{sh(h.implied_shares)}</td>
                  <td className="py-2.5 px-2 2xl:px-3 text-right font-mono text-[0.9rem] text-ink">
                    {usd0(h.implied_value)}
                    <div className={`sm:hidden text-[0.68rem] ${h.pnl_pct >= 0 ? 'text-positive' : 'text-negative'}`}>{h.pnl_pct >= 0 ? '+' : ''}{h.pnl_pct}%</div>
                  </td>
                  <td className="py-2.5 px-2 2xl:px-3 font-mono text-[0.72rem] whitespace-nowrap">
                    {isMax ? (
                      <div className="min-w-[84px] text-claret">
                        <div className="mb-0.5">day {h.days_held}/{h.hold_days} · ~{h.days_left}d</div>
                        {/* Hold-clock: progress through the 29-day time-stop; near-exit turns solid claret */}
                        <div className="h-1 bg-paper-deep rounded overflow-hidden">
                          <div className={`h-full ${h.days_left <= 5 ? 'bg-claret' : 'bg-claret/50'}`}
                               style={{ width: `${Math.min(100, Math.round((h.days_held / (h.hold_days || 29)) * 100))}%` }} />
                        </div>
                      </div>
                    ) : (() => {
                      // Preserver compact: a thin cushion gauge — the standalone gauge distilled
                      // to one row. Band = stop (left edge) → high-water mark (right edge); green
                      // fill = cushion up to now; thin tick = entry; solid marker = now. Thin
                      // cushion (near the stop) turns claret.
                      const lo = Number(h.trailing_stop_level) || 0;
                      const hi = Math.max(Number(h.high_water_mark) || 0, lo + 0.01);
                      const now = Number(h.price) || lo;
                      const entry = Number(h.entry_price) || lo;
                      const clampPct = (v) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
                      const nowPct = clampPct(now);
                      const entryPct = clampPct(entry);
                      const cushion = now > 0 ? Math.max(0, (now - lo) / now * 100) : 0;
                      const thin = cushion <= 8;
                      return (
                        <div className="min-w-[84px]">
                          <div className={`mb-1 ${thin ? 'text-claret' : 'text-ink-mute'}`}>{Math.round(cushion)}% to stop</div>
                          <div className="relative h-2 bg-paper-deep rounded">
                            {/* cushion fill: stop → now */}
                            <div className={`absolute inset-y-0 left-0 rounded-l ${thin ? 'bg-claret/40' : 'bg-positive/35'}`} style={{ width: `${nowPct}%` }} />
                            {/* entry tick */}
                            <div className="absolute top-1/2 -translate-y-1/2 h-[11px] w-px bg-ink-mute/70" style={{ left: `${entryPct}%` }} title={`Entry $${entry.toFixed(2)}`} />
                            {/* now marker */}
                            <div className={`absolute top-1/2 -translate-y-1/2 h-3.5 w-[2px] rounded ${thin ? 'bg-claret' : 'bg-ink'}`} style={{ left: `calc(${nowPct}% - 1px)` }} title={`Now $${now.toFixed(2)}`} />
                          </div>
                          <div className="flex justify-between mt-0.5 text-[0.52rem] leading-none text-ink-light">
                            <span>stop ${Math.round(lo)}</span><span>high ${Math.round(hi)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className={`py-2.5 px-2 2xl:px-3 text-right font-mono text-[0.82rem] whitespace-nowrap hidden sm:table-cell ${h.pnl_pct >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {h.pnl_pct >= 0 ? '+' : ''}{h.pnl_pct}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      ) : (
        <div>
          {(book.holdings || []).length === 0 ? (
            <div className="px-4 sm:px-5 py-6 text-center font-display italic text-ink-mute text-[0.9rem]" style={{ fontVariationSettings: '"opsz" 24' }}>
              The book is in cash right now — no positions held.
            </div>
          ) : (book.holdings || []).map((h) => (
            <div
              key={h.symbol}
              onClick={() => onRowClick && onRowClick(h)}
              className={`px-4 sm:px-5 py-4 border-b border-rule/60 ${onRowClick ? 'cursor-pointer hover:bg-paper-deep transition-colors' : ''}`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-2.5">
                <div className="flex items-baseline gap-2 min-w-0">
                  <HeldDot sym={h.symbol} />
                  <span className="font-display text-[1.1rem] font-medium text-ink" style={{ fontVariationSettings: '"opsz" 32' }}>{h.symbol}</span>
                  {h.is_new && <span className="font-mono text-[0.55rem] uppercase tracking-[0.14em] text-claret border border-claret/40 px-1 py-0.5">New</span>}
                  <span className="font-mono text-[0.68rem] text-ink-mute whitespace-nowrap">{h.weight_pct}% of book</span>
                </div>
                <span className={`font-mono text-[0.95rem] whitespace-nowrap ${h.pnl_pct >= 0 ? 'text-positive' : 'text-negative'}`}>{h.pnl_pct >= 0 ? '+' : ''}{h.pnl_pct}%</span>
              </div>
              <HoldingGauge entry={h.entry_price} now={h.price} hwm={h.high_water_mark} stop={h.trailing_stop_level} />
              <div className="flex items-center justify-between gap-2 mt-2.5 font-mono text-[0.7rem] text-ink-mute">
                <span>entry ${h.entry_price?.toFixed(2)} → now ${h.price?.toFixed(2)}</span>
                <span className="text-ink whitespace-nowrap">{sh(h.implied_shares)} sh · {usd0(h.implied_value)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Breakout Radar — names approaching a 50-day-high breakout (what the book is about to
          enter). Replaces the watchlist for Maximizer. */}
      {(radar || []).length > 0 && (
        <div className="border-t border-rule">
          <div className="px-4 sm:px-5 py-2.5 flex items-baseline justify-between">
            <span className="font-display text-[0.95rem] font-medium tracking-tight text-ink" style={{ fontVariationSettings: '"opsz" 32' }}>Breakout Radar</span>
            <span className="font-display italic text-[0.8rem] text-claret" style={{ fontVariationSettings: '"opsz" 24' }}>approaching trigger</span>
          </div>
          <div className="flex flex-wrap gap-2 px-4 sm:px-5 pb-3">
            {radar.map((r) => (
              <div
                key={r.symbol}
                onClick={() => onRowClick && onRowClick({ symbol: r.symbol, price: r.price, source: 'breakout' })}
                className="border border-rule rounded px-3 py-2 bg-white cursor-pointer hover:bg-paper-deep transition-colors"
              >
                <div className="font-display text-[0.92rem] font-medium text-ink" style={{ fontVariationSettings: '"opsz" 32' }}>{r.symbol}</div>
                <div className="font-mono text-[0.62rem] text-ink-mute">{r.pct_below_50d_high}% below high · vol {r.vol_ratio}x</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 sm:px-5 py-3 border-t border-rule flex flex-wrap items-center justify-between gap-2 text-[0.7rem] text-ink-mute">
        <span>As of {book.as_of || '—'} · mirror the book to match its results — partial-following diverges.</span>
        <span className="font-display italic" style={{ fontVariationSettings: '"opsz" 24' }}>Signals only — execute via your broker</span>
      </div>
    </div>
  );
}
